import axios from 'axios'
import { ElMessage } from 'element-plus'
import 'element-plus/es/components/message/style/css'

import i18n from '@/i18n'

/**
 * Sekin va uzilib turadigan internet uchun moslashtirilgan axios instansiyasi.
 *
 * Oldingi holatda uchta muammo bor edi:
 *
 *   1. timeout 15 s. Sekin ulanishda katta javob (masalan mijozlar ro'yxati)
 *      shu vaqtga sig'masdi va foydalanuvchi xato ko'rardi, holbuki so'rov
 *      aslida ishlayotgan edi.
 *
 *   2. Qayta urinish yo'q. Bitta yo'qolgan paket = ekranda xato.
 *
 *   3. ENG JIDDIYSI: refresh so'rovi HAR QANDAY sababdan yiqilsa —
 *      tarmoq uzilgani, timeout bo'lgani ham — foydalanuvchi login sahifasiga
 *      otib tashlanardi va kiritilayotgan ma'lumot yo'qolardi. Sekin
 *      internetda bu eng ko'p uchraydigan nosozlik: token haqiqiy, tarmoq esa
 *      bir soniya uzilgan. Endi faqat server tokenni RAD ETSA chiqariladi.
 */

// Sekin ulanishda katta ro'yxatlar 15 s ga sig'maydi. 30 s — real chegara;
// undan uzuni foydalanuvchi uchun "osilib qoldi" degani.
const DEFAULT_TIMEOUT = 30000

// Fon so'rovlari (polling) tezroq taslim bo'lishi kerak: ular navbatga
// to'planib, foydalanuvchining haqiqiy bosishlarini kutishga majbur qilmasin.
export const BACKGROUND_TIMEOUT = 12000

const MAX_RETRIES = 2

const instance = axios.create({
  baseURL: process.env.VUE_APP_API_URL || '',
  timeout: DEFAULT_TIMEOUT,
})

// ---------------------------------------------------------------------------
// Bir xil GET so'rovlarini birlashtirish
// ---------------------------------------------------------------------------
// Nega: bir necha komponent bir vaqtda bir xil ma'lumotni so'rashi mumkin
// (masalan notification polleri ham, chat store ham unread-total ni). Sekin
// tarmoqda bu ikkinchi so'rov bekorga ulanish sloti egallaydi.
const inFlight = new Map()

const keyOf = (config) =>
  `${config.method}|${config.baseURL || ''}${config.url}|${JSON.stringify(config.params || {})}`

// ---------------------------------------------------------------------------
// Qayta urinish
// ---------------------------------------------------------------------------
const RETRYABLE_STATUS = [502, 503, 504]

const isRetryable = (error) => {
  const cfg = error.config
  if (!cfg) return false
  // Faqat idempotent metodlar. POST/PATCH/DELETE ni qayta yuborish
  // ikki marta bajarilishiga olib kelishi mumkin.
  const method = (cfg.method || 'get').toLowerCase()
  if (method !== 'get' && method !== 'head') return false
  if (error.code === 'ERR_CANCELED' || error.name === 'AbortError') return false

  // Tarmoq xatosi yoki timeout — javob umuman kelmagan.
  if (!error.response) return true
  return RETRYABLE_STATUS.includes(error.response.status)
}

const backoffDelay = (attempt) => {
  // 600 ms, 1800 ms — ustiga ±20% tasodifiy siljish, bir vaqtda yiqilgan
  // so'rovlar birgalikda qaytib tarmoqni yana to'ldirmasin.
  const base = 600 * Math.pow(3, attempt)
  return Math.round(base * (0.8 + Math.random() * 0.4))
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

// ---------------------------------------------------------------------------
// Request interceptor
// ---------------------------------------------------------------------------
instance.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    // `!config.timeout` EMAS. axios instansiya standartlarini so'rov
    // interceptoridan OLDIN qo'shib qo'yadi, ya'ni bu yerda config.timeout
    // allaqachon DEFAULT_TIMEOUT (30000) bo'lib turadi va `!config.timeout`
    // HECH QACHON rost bo'lmaydi. O'lchangan: uchala urinishda ham
    // interceptor 30000 ni ko'rdi. Ya'ni BACKGROUND_TIMEOUT o'lik kod edi va
    // fon so'rovlari to'liq 30 soniya kutardi — mo'ljallanganning teskarisi.
    //
    // Standart qiymat bilan solishtiramiz: chaqiruvchi o'zi boshqa timeout
    // bergan bo'lsa, uni buzmaymiz.
    if (config.background && config.timeout === DEFAULT_TIMEOUT) {
      config.timeout = BACKGROUND_TIMEOUT
    }
    return config
  },
  (error) => Promise.reject(error),
  // `synchronous: true` — BEZAK EMAS, XATONI CHETLAB O'TISH.
  //
  // axios 1.13.3 da `Axios.prototype.request` ikki xil yo'ldan yuradi. Barcha
  // so'rov interceptorlari sinxron bo'lsa — oddiy va to'g'ri yo'l. Aks holda
  // (standart holat) asinxron zanjir quriladi (axios.cjs:4826-4834):
  //
  //     promise = promise.then(chain[i++])
  //         .then(result => { prevResult = result !== undefined ? result : prevResult })
  //         .catch(chain[i++])
  //         .then(() => prevResult)
  //
  // Oxirgi `.then(() => prevResult)` javob-XATO handleri qaytargan qiymatni
  // TASHLAB YUBORADI, chunki `prevResult` faqat muvaffaqiyat shoxida
  // yangilanadi. Natijada `return instance(originalRequest)` javob emas,
  // CONFIG obyektini qaytaradi.
  //
  // O'lchangan (haqiqiy axios 1.13.3 va lokal HTTP server bilan):
  //     so'rov-interceptori yo'q            -> JAVOB(200)
  //     bor, standart                       -> config
  //     bor, standart + await               -> config
  //     bor, { synchronous: true }          -> JAVOB(200)
  //
  // Bu ikkita yo'lga ta'sir qiladi: qayta urinish (yangi) va 401 dan keyingi
  // token yangilash (DASTLABKI COMMITDAN BERI BOR). Token umri 5 soat, ya'ni
  // ish kuni davomida har bir foydalanuvchining kamida bitta so'rovi javob
  // o'rniga config olardi va chaqiruvchi `response.data` da undefined ko'rardi.
  //
  // Bizning interceptorimiz haqiqatan sinxron: hech narsa kutmaydi, config
  // qaytaradi. Shuning uchun bu bayroq to'g'ri.
  { synchronous: true }
)

// ---------------------------------------------------------------------------
// Response interceptor
// ---------------------------------------------------------------------------
let refreshPromise = null

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // --- 401: tokenni yangilash -------------------------------------------
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true

      const refreshToken = sessionStorage.getItem('refreshToken')
      if (!refreshToken) {
        logout()
        return Promise.reject(error)
      }

      if (!refreshPromise) {
        refreshPromise = axios
          .post('/api/users/refresh/', { refresh: refreshToken }, { timeout: DEFAULT_TIMEOUT })
          .then((res) => {
            const { access } = res.data
            sessionStorage.setItem('token', access)
            return access
          })
          .catch((err) => {
            // MUHIM FARQ: tokenni faqat SERVER rad etsa o'chiramiz.
            // Tarmoq uzilishi, timeout yoki 5xx — bu tokenning yaroqsizligi
            // EMAS. Bunda foydalanuvchini chiqarib tashlash sekin internetda
            // eng ko'p uchraydigan va eng bezor nosozlik edi: kiritilayotgan
            // ma'lumot yo'qolardi.
            const st = err.response?.status
            if (st === 400 || st === 401 || st === 403) {
              logout()
            }
            throw err
          })
          .finally(() => {
            refreshPromise = null
          })
      }

      try {
        const newToken = await refreshPromise
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return instance(originalRequest)
      } catch (err) {
        return Promise.reject(err)
      }
    }

    // --- Bekor qilingan so'rovlar ------------------------------------------
    if (error.code === 'ERR_CANCELED' || error.name === 'AbortError') {
      return Promise.reject(error)
    }

    // --- Qayta urinish -----------------------------------------------------
    if (isRetryable(error)) {
      originalRequest._attempt = (originalRequest._attempt || 0) + 1
      if (originalRequest._attempt <= MAX_RETRIES) {
        // Offline bo'lsa kutish behuda — `online` hodisasini kutamiz.
        if (navigator.onLine === false) {
          await new Promise((resolve) => {
            const on = () => { window.removeEventListener('online', on); resolve() }
            window.addEventListener('online', on)
            setTimeout(() => { window.removeEventListener('online', on); resolve() }, 15000)
          })
        } else {
          await sleep(backoffDelay(originalRequest._attempt - 1))
        }
        return instance(originalRequest)
      }
    }

    // --- Xabar ko'rsatish --------------------------------------------------
    // Fon so'rovlari jim yiqiladi: polling xatosi uchun ekranga xabar
    // chiqarish sekin internetda foydalanuvchini ko'mib tashlaydi.
    if (!originalRequest?.background) {
      const status = error.response?.status
      if (!error.response) {
        // Javob umuman kelmadi — tarmoq muammosi.
        ElMessage.closeAll()
        ElMessage({
          type: 'warning',
          message: navigator.onLine === false
            ? i18n.global.t('network.offline')
            : i18n.global.t('network.slow'),
          duration: 4000,
          grouping: true,
        })
      } else if (status === 500 || status === 503) {
        const message =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          i18n.global.t('network.serverError')
        ElMessage.error(message)
      } else if (status === 403) {
        ElMessage.error(i18n.global.t('network.forbidden'))
      }
    }

    return Promise.reject(error)
  }
)

function logout() {
  sessionStorage.removeItem('token')
  sessionStorage.removeItem('refreshToken')
  if (window.location.pathname !== '/login') {
    window.location.href = '/login'
  }
}

// ---------------------------------------------------------------------------
// GET larni birlashtiruvchi o'ram
// ---------------------------------------------------------------------------
// Interceptor darajasida qilib bo'lmaydi (axios bir xil so'rovni birlashtirish
// nuqtasini bermaydi), shuning uchun `get` ni o'raymiz.
const rawGet = instance.get.bind(instance)

instance.get = function dedupedGet(url, config = {}) {
  if (config.dedupe === false) return rawGet(url, config)

  const key = keyOf({ method: 'get', baseURL: instance.defaults.baseURL, url, params: config.params })
  const existing = inFlight.get(key)
  if (existing) return existing

  const p = rawGet(url, config).finally(() => inFlight.delete(key))
  inFlight.set(key, p)
  return p
}

export default instance
